// Platform admin (design flag 10): kept apart from project work; visible only to PLATFORM_ADMINS.
import { useMutation, useQuery } from "convex/react";
import { Fragment, useState } from "react";
import { api } from "../../convex/_generated/api";
import { Badge, Button, Card, PageHeader, Spinner, useRun } from "@/components/ui";

export function Platform() {
  const tenants = useQuery(api.account.platformTenants);
  const setStatus = useMutation(api.account.setTenantStatus);
  const setComped = useMutation(api.billing.setComped);
  const run = useRun();
  const [open, setOpen] = useState<string | null>(null);
  if (!tenants) return <Spinner />;
  return (
    <div className="mx-auto max-w-5xl space-y-4 p-6">
      <PageHeader title="Platform" sub="All organisations on this deployment, with their users, projects and subscription. Click an organisation to see who is in it. Beta organisations (from before billing) keep free access; Comp gives any organisation free access." />
      <Card bodyClass="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2">Organisation</th>
              <th className="px-4 py-2">Contact</th>
              <th className="px-4 py-2 text-right">Users</th>
              <th className="px-4 py-2 text-right">Projects</th>
              <th className="px-4 py-2">Subscription</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <Fragment key={t._id}>
              <tr className="cursor-pointer border-t border-slate-100 hover:bg-slate-50" onClick={() => setOpen(open === t._id ? null : t._id)}>
                <td className="px-4 py-2 font-medium">
                  <span className="mr-1.5 inline-block w-3 text-slate-400">{open === t._id ? "▾" : "▸"}</span>
                  {t.name}
                </td>
                <td className="px-4 py-2">{t.email}</td>
                <td className="num px-4 py-2 text-right">{t.users}</td>
                <td className="num px-4 py-2 text-right">{t.projects}</td>
                <td className="px-4 py-2">
                  {t.billing ? (
                    <Badge tone={["active", "trialing", "comped"].includes(t.billing.status) ? "green" : t.billing.status === "past_due" ? "amber" : "red"}>
                      {t.billing.status === "comped" ? "comped" : `${t.billing.status} · ${t.billing.seats} seat${t.billing.seats === 1 ? "" : "s"}`}
                    </Badge>
                  ) : (
                    <span className="text-xs text-slate-500">beta (free)</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <Badge tone={t.status === "active" ? "green" : "red"}>{t.status}</Badge>
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right">
                  {t.billing && (
                    <Button size="sm" variant="ghost" className="mr-1" onClick={(e) => { e.stopPropagation(); void run(() => setComped({ tenantId: t._id, comped: t.billing!.status !== "comped" })); }}>
                      {t.billing.status === "comped" ? "Uncomp" : "Comp"}
                    </Button>
                  )}
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); void run(() => setStatus({ tenantId: t._id, status: t.status === "active" ? "suspended" : "active" })); }}>
                    {t.status === "active" ? "Suspend" : "Reactivate"}
                  </Button>
                </td>
              </tr>
              {open === t._id && (
                <tr className="bg-slate-50/60">
                  <td colSpan={7} className="px-4 pb-3 pt-1">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <div className="mb-1 text-xs font-semibold text-slate-500">Users</div>
                        {t.members.length === 0 && <div className="text-slate-400">None</div>}
                        {t.members.map((m) => (
                          <div key={m.email} className="flex justify-between gap-2">
                            <span>{m.email}</span>
                            <span className="text-slate-500">{m.role}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <div className="mb-1 text-xs font-semibold text-slate-500">Projects</div>
                        {t.projectList.length === 0 && <div className="text-slate-400">None</div>}
                        {t.projectList.map((p) => (
                          <div key={p.code + p.created} className="flex justify-between gap-2">
                            <span>
                              <span className="font-medium">{p.code}</span> {p.name}
                            </span>
                            <span className="text-slate-500">{new Date(p.created).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" })}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
      <Leads />
    </div>
  );
}

/** Demo requests and messages from the website's forms. */
function Leads() {
  const leads = useQuery(api.leads.list);
  const setHandled = useMutation(api.leads.setHandled);
  const run = useRun();
  if (!leads) return null;
  return (
    <Card title={`Website requests (${leads.filter((l) => !l.handled).length} open)`} bodyClass="p-0">
      {!leads.length ? (
        <p className="p-4 text-sm text-slate-500">No demo requests or messages from the website yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2">When</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">From</th>
              <th className="px-4 py-2">Message</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l._id} className={`border-t border-slate-100 align-top ${l.handled ? "text-slate-400" : ""}`}>
                <td className="whitespace-nowrap px-4 py-2">{new Date(l._creationTime).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                <td className="px-4 py-2">
                  <Badge tone={l.kind === "demo" ? "blue" : "slate"}>{l.kind === "demo" ? "demo" : "contact"}</Badge>
                </td>
                <td className="px-4 py-2">
                  <div className="font-medium">{l.name}</div>
                  <a className="text-brand-600 underline" href={`mailto:${l.email}`}>
                    {l.email}
                  </a>
                  {l.company && <div>{l.company}</div>}
                  {l.phone && <div>{l.phone}</div>}
                </td>
                <td className="whitespace-pre-wrap px-4 py-2">{l.message}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => run(() => setHandled({ id: l._id, handled: !l.handled }))}>
                    {l.handled ? "Reopen" : "Done"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
